import { useCallback, useState } from 'react';

import { useApolloClient } from '@apollo/client/react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { trackAnalyticsExport } from '@/modules/analytics/utils/analyticsTracking';
import { buildExportFilename } from '@/modules/analytics/utils/exportFilename';
import { PageName } from '@/shared/types/segment';
import { downloadCsv, downloadJson } from '@/util/fileManager';

import {
  ACCOUNT_RESPONSE_TIMES_SUMMARY,
  type OverviewResponseTimesData,
} from './useOverviewResponseTimes';
import {
  ACCOUNT_CONVERSATIONS_SUMMARY,
  type OverviewSessionMetricsData,
} from './useOverviewSessionMetrics';
import { ACCOUNT_CONVERSATIONS_TIMESERIES } from './useOverviewTimeseries';
import { useOverviewVariables } from './useOverviewVariables';

type ExportFormat = 'json' | 'csv';
type WidgetId = 'overview' | 'meaningful_conversations' | 'time_based_performance';

interface TimeseriesRow {
  time_bucket: string;
  avg_csat_score: number | null;
  containment_rate: number | null;
  total_conversations: number | null;
}

const buildOverviewPayload = (
  sessionMetrics: OverviewSessionMetricsData | null,
  timeseriesRows: TimeseriesRow[] | null
) => {
  if (!sessionMetrics && !timeseriesRows) {
    return null;
  }

  return {
    kpi: sessionMetrics
      ? {
          avg_ai_csat: sessionMetrics.avg_ai_csat,
          avg_ai_csat_insights: sessionMetrics.avg_ai_csat_insights,
          ai_csat_insights_count: sessionMetrics.ai_csat_insights_count,
          avg_csat_user_rating: sessionMetrics.avg_csat_user_rating,
          csat_user_rating_count: sessionMetrics.csat_user_rating_count,
          containment_rate: sessionMetrics.containment_rate,
          total_conversations: sessionMetrics.total_conversations,
        }
      : null,
    timeseries: (timeseriesRows ?? []).map(
      ({ time_bucket, avg_csat_score, containment_rate, total_conversations }) => ({
        time_bucket,
        avg_csat_score,
        containment_rate,
        total_conversations,
      })
    ),
  };
};

const buildMeaningfulPayload = (sessionMetrics: OverviewSessionMetricsData | null) => {
  if (!sessionMetrics) {
    return null;
  }

  return {
    total_conversations: sessionMetrics.total_conversations,
    meaningful_conversations: sessionMetrics.meaningful_conversations,
    nonmeaningful_conversations: sessionMetrics.nonmeaningful_conversations,
    meaningful_brain_conversations: sessionMetrics.meaningful_brain_conversations,
    meaningful_collection_conversations: sessionMetrics.meaningful_collection_conversations,
  };
};

const buildTimeBasedPayload = (
  responseTimes: OverviewResponseTimesData | null,
  sessionMetrics: OverviewSessionMetricsData | null
) => {
  if (!responseTimes && !sessionMetrics) {
    return null;
  }

  return {
    response_times: responseTimes
      ? {
          avg_response_time_minutes: responseTimes.avg_response_time,
          avg_ai_response_time_minutes: responseTimes.avg_ai_response_time,
          avg_human_response_time_minutes: responseTimes.avg_human_response_time,
        }
      : null,
    session_length: sessionMetrics
      ? {
          median_ai_session_length_seconds: sessionMetrics.median_ai_session_length_seconds,
          median_human_session_length_seconds: sessionMetrics.median_human_session_length_seconds,
        }
      : null,
  };
};

const flattenOverviewForCsv = (
  sessionMetrics: OverviewSessionMetricsData | null,
  timeseriesRows: TimeseriesRow[] | null
): Record<string, unknown>[] => {
  const rows: Record<string, unknown>[] = [];

  if (sessionMetrics) {
    rows.push({
      row_type: 'kpi',
      avg_ai_csat: sessionMetrics.avg_ai_csat,
      containment_rate: sessionMetrics.containment_rate,
      total_conversations: sessionMetrics.total_conversations,
      avg_ai_csat_insights: sessionMetrics.avg_ai_csat_insights,
      ai_csat_insights_count: sessionMetrics.ai_csat_insights_count,
      avg_csat_user_rating: sessionMetrics.avg_csat_user_rating,
      csat_user_rating_count: sessionMetrics.csat_user_rating_count,
    });
  }

  if (timeseriesRows) {
    for (const row of timeseriesRows) {
      rows.push({
        row_type: 'timeseries',
        time_bucket: row.time_bucket,
        avg_csat_score: row.avg_csat_score,
        containment_rate: row.containment_rate,
        total_conversations: row.total_conversations,
      });
    }
  }

  return rows;
};

const flattenMeaningfulForCsv = (
  sessionMetrics: OverviewSessionMetricsData | null
): Record<string, unknown>[] => {
  if (!sessionMetrics) {
    return [];
  }

  return [
    {
      total_conversations: sessionMetrics.total_conversations,
      meaningful_conversations: sessionMetrics.meaningful_conversations,
      nonmeaningful_conversations: sessionMetrics.nonmeaningful_conversations,
      meaningful_brain_conversations: sessionMetrics.meaningful_brain_conversations,
      meaningful_collection_conversations: sessionMetrics.meaningful_collection_conversations,
    },
  ];
};

const flattenTimeBasedForCsv = (
  responseTimes: OverviewResponseTimesData | null,
  sessionMetrics: OverviewSessionMetricsData | null
): Record<string, unknown>[] => {
  if (!responseTimes && !sessionMetrics) {
    return [];
  }

  return [
    {
      avg_response_time_minutes: responseTimes?.avg_response_time ?? null,
      avg_ai_response_time_minutes: responseTimes?.avg_ai_response_time ?? null,
      avg_human_response_time_minutes: responseTimes?.avg_human_response_time ?? null,
      median_ai_session_length_seconds: sessionMetrics?.median_ai_session_length_seconds ?? null,
      median_human_session_length_seconds:
        sessionMetrics?.median_human_session_length_seconds ?? null,
    },
  ];
};

export const useOverviewExport = () => {
  const client = useApolloClient();
  const { variables } = useOverviewVariables();
  const { t } = useTranslation();
  const [isExporting, setIsExporting] = useState(false);

  const readSessionMetrics = useCallback((): OverviewSessionMetricsData | null => {
    try {
      const result = client.readQuery<{ rows: OverviewSessionMetricsData[] }>({
        query: ACCOUNT_CONVERSATIONS_SUMMARY,
        variables,
      });
      return result?.rows?.[0] ?? null;
    } catch {
      return null;
    }
  }, [client, variables]);

  const readResponseTimes = useCallback((): OverviewResponseTimesData | null => {
    try {
      const result = client.readQuery<{ rows: OverviewResponseTimesData[] }>({
        query: ACCOUNT_RESPONSE_TIMES_SUMMARY,
        variables,
      });
      return result?.rows?.[0] ?? null;
    } catch {
      return null;
    }
  }, [client, variables]);

  const readTimeseries = useCallback((): TimeseriesRow[] | null => {
    try {
      const result = client.readQuery<{ rows: TimeseriesRow[] }>({
        query: ACCOUNT_CONVERSATIONS_TIMESERIES,
        variables,
      });
      return result?.rows ?? null;
    } catch {
      return null;
    }
  }, [client, variables]);

  const exportWidget = useCallback(
    (widgetId: WidgetId, format: ExportFormat) => {
      try {
        setIsExporting(true);
        const filename = buildExportFilename(`overview_${widgetId}`);

        const sessionMetrics = readSessionMetrics();
        const responseTimes = readResponseTimes();
        const timeseriesRows = readTimeseries();

        trackAnalyticsExport(PageName.ANALYTICS_OVERVIEW, widgetId, format);

        if (format === 'json') {
          let data: unknown = null;
          switch (widgetId) {
            case 'overview':
              data = buildOverviewPayload(sessionMetrics, timeseriesRows);
              break;
            case 'meaningful_conversations':
              data = buildMeaningfulPayload(sessionMetrics);
              break;
            case 'time_based_performance':
              data = buildTimeBasedPayload(responseTimes, sessionMetrics);
              break;
          }
          if (data) {
            downloadJson(data, filename);
          }
        } else {
          let rows: Record<string, unknown>[] = [];
          switch (widgetId) {
            case 'overview':
              rows = flattenOverviewForCsv(sessionMetrics, timeseriesRows);
              break;
            case 'meaningful_conversations':
              rows = flattenMeaningfulForCsv(sessionMetrics);
              break;
            case 'time_based_performance':
              rows = flattenTimeBasedForCsv(responseTimes, sessionMetrics);
              break;
          }
          if (rows.length > 0) {
            downloadCsv(rows, filename);
          }
        }
      } catch {
        toast.error(t('analytics.export_error'));
      } finally {
        setIsExporting(false);
      }
    },
    [readSessionMetrics, readResponseTimes, readTimeseries, t]
  );

  const exportAll = useCallback(
    (format: ExportFormat) => {
      try {
        setIsExporting(true);
        const filename = buildExportFilename('overview_all');

        const sessionMetrics = readSessionMetrics();
        const responseTimes = readResponseTimes();
        const timeseriesRows = readTimeseries();

        trackAnalyticsExport(PageName.ANALYTICS_OVERVIEW, 'overview_all', format);

        if (format === 'json') {
          const merged: Record<string, unknown> = {};

          const overview = buildOverviewPayload(sessionMetrics, timeseriesRows);
          if (overview) {
            merged.overview = overview;
          }

          const meaningful = buildMeaningfulPayload(sessionMetrics);
          if (meaningful) {
            merged.meaningful_conversations = meaningful;
          }

          const timeBased = buildTimeBasedPayload(responseTimes, sessionMetrics);
          if (timeBased) {
            merged.time_based_performance = timeBased;
          }

          if (Object.keys(merged).length > 0) {
            downloadJson(merged, filename);
          }
        } else {
          const allRows: Record<string, unknown>[] = [];

          for (const row of flattenOverviewForCsv(sessionMetrics, timeseriesRows)) {
            allRows.push({ widget: 'overview', ...row });
          }
          for (const row of flattenMeaningfulForCsv(sessionMetrics)) {
            allRows.push({ widget: 'meaningful_conversations', ...row });
          }
          for (const row of flattenTimeBasedForCsv(responseTimes, sessionMetrics)) {
            allRows.push({ widget: 'time_based_performance', ...row });
          }

          if (allRows.length > 0) {
            downloadCsv(allRows, filename);
          }
        }
      } catch {
        toast.error(t('analytics.export_error'));
      } finally {
        setIsExporting(false);
      }
    },
    [readSessionMetrics, readResponseTimes, readTimeseries, t]
  );

  return { exportWidget, exportAll, isExporting };
};
